import { type Entity } from '../entities/Entity';

/**
 * A many-to-many index from some key to the entities carrying it.
 *
 * The scene keeps one of these per thing worth querying by, so component lookup and tag lookup share a
 * single implementation instead of two that can drift apart. Emptied buckets are dropped rather than
 * left behind, so a scene that churns through tags does not accumulate an entry for every tag it has
 * ever seen.
 */
export class EntityIndex<TKey> {
  readonly #byKey = new Map<TKey, Set<Entity>>();

  /** Records that an entity now carries this key. Adding the same pair twice is a no-op. */
  add(key: TKey, entity: Entity): void {
    let matches = this.#byKey.get(key);
    if (matches === undefined) {
      matches = new Set<Entity>();
      this.#byKey.set(key, matches);
    }
    matches.add(entity);
  }

  /** Records that an entity no longer carries this key. Removing an unknown pair is a no-op. */
  remove(key: TKey, entity: Entity): void {
    const matches = this.#byKey.get(key);
    if (matches === undefined) {
      return;
    }
    matches.delete(entity);
    if (matches.size === 0) {
      this.#byKey.delete(key);
    }
  }

  /** Every entity carrying this key, as a fresh array in insertion order. */
  get(key: TKey): readonly Entity[] {
    const matches = this.#byKey.get(key);
    return matches === undefined ? [] : [...matches];
  }
}
